import React from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";

const sections = [
  {
    number: "1",
    title: "About Biznep",
    paragraphs: [
      "Biznep is a business listing and review platform that allows users to:",
    ],
    bullets: [
      "Discover local businesses in Nepal",
      "Post ratings and reviews",
      "Upload photos and share experiences",
      "Connect with business owners and service providers",
    ],
    afterBullets:
      "Biznep acts as a platform for information sharing and does not own, operate, or manage any of the listed businesses.",
  },
  {
    number: "2",
    title: "Eligibility",
    paragraphs: ["To use Biznep, you must:"],
    bullets: [
      "Be at least 16 years old;",
      "Use the Service for lawful purposes only;",
      "Agree to provide accurate and truthful information during registration.",
    ],
    afterBullets:
      "If you use Biznep on behalf of a business, you represent that you are authorized to bind that business to these Terms.",
  },
  {
    number: "3",
    title: "Account Registration",
    paragraphs: [
      "You may be required to create an account using your email, phone number, or social login. You are responsible for:",
    ],
    bullets: [
      "Maintaining the confidentiality of your login credentials;",
      "All activities that occur under your account;",
      "Notifying us immediately of any unauthorized use.",
    ],
    afterBullets:
      "We reserve the right to suspend or terminate any account that violates these Terms.",
  },
  {
    number: "4",
    title: "User Content",
    paragraphs: [
      'You may post reviews, photos, ratings, comments, and other content ("User Content"). By posting, you grant Biznep a non-exclusive, worldwide, royalty-free, perpetual license to use, display, and distribute your content on the platform.',
      "You agree that:",
    ],
    bullets: [
      "Your content is accurate and based on your real experience;",
      "You will not post offensive, defamatory, false, or misleading content;",
      "You will not upload copyrighted material without permission.",
    ],
    afterBullets:
      "Biznep reserves the right to remove or moderate any content that violates these Terms or Nepali law.",
  },
  {
    number: "5",
    title: "Business Listings",
    paragraphs: [
      "Business owners may claim or create listings to promote their services. By doing so, you agree to:",
    ],
    bullets: [
      "Provide accurate and updated information;",
      "Not post fake or duplicate listings;",
      "Not misuse reviews or ratings to mislead users.",
    ],
    afterBullets:
      "Biznep may verify listings and request supporting documents when necessary.",
  },
  {
    number: "6",
    title: "Prohibited Conduct",
    paragraphs: ["You agree not to:"],
    bullets: [
      "Use Biznep for illegal or fraudulent purposes;",
      "Post spam, advertisements, or misleading content;",
      "Attempt to hack, scrape, or interfere with the Service;",
      "Harass, threaten, or abuse other users.",
    ],
    afterBullets:
      "Violation of these terms may result in account suspension or permanent ban.",
  },
  {
    number: "7",
    title: "Intellectual Property",
    paragraphs: [
      "All logos, trademarks, design, and software on Biznep are the property of Biznep Pvt. Ltd. or its licensors. You may not copy, modify, or distribute any part of the Service without written permission.",
    ],
  },
  {
    number: "8",
    title: "Privacy Policy",
    paragraphs: [
      "Your privacy is important to us. Our Privacy Policy explains how we collect, use, and protect your data. By using Biznep, you consent to our data practices as described in the Privacy Policy.",
    ],
    privacyLink: true,
  },
  {
    number: "9",
    title: "Limitation of Liability",
    paragraphs: [
      "Biznep provides the Service “as is” and makes no guarantees about the accuracy, reliability, or availability of business information. We are not liable for:",
    ],
    bullets: [
      "Any losses or damages arising from your use of the Service;",
      "Interactions or transactions between users and businesses;",
      "Content posted by third parties.",
    ],
  },
  {
    number: "10",
    title: "Termination",
    paragraphs: [
      "We may suspend or terminate your account or access to Biznep at any time if you violate these Terms or applicable laws. You may stop using the Service at any time by deleting your account.",
    ],
  },
  {
    number: "11",
    title: "Modifications",
    paragraphs: [
      "Biznep may update these Terms from time to time. The latest version will always be available in the app or on our website. Your continued use of the Service after changes means you accept the revised Terms.",
    ],
  },
  {
    number: "12",
    title: "Governing Law",
    paragraphs: [
      "These Terms shall be governed by and construed in accordance with the laws of Nepal. Any disputes shall be subject to the exclusive jurisdiction of the courts in Kathmandu, Nepal.",
    ],
  },
  {
    number: "13",
    title: "Contact Information",
    paragraphs: [
      "If you have any questions or concerns about these Terms, please contact us at:",
    ],
    email: "support@biznep.net",
  },
];

function BulletList({ items }) {
  return (
    <ul className="mt-3 space-y-2">
      {items.map((item) => (
        <li key={item} className="flex items-start gap-3">
          <span className="mt-[9px] h-[6px] w-[6px] shrink-0 rounded-full bg-[#D32323]" />

          <span className="text-[16px] leading-[1.65] text-[#59677c]">
            {item}
          </span>
        </li>
      ))}
    </ul>
  );
}

function TermsSection({ section }) {
  return (
    <section className="border-b border-[#e8edf3] py-8 last:border-b-0">
      <h2 className="text-[22px] font-bold leading-[1.3] tracking-[-0.3px] text-[#17233b]">
        {section.number}. {section.title}
      </h2>

      {section.paragraphs?.map((paragraph) => (
        <p
          key={paragraph}
          className="mt-4 text-[16px] leading-[1.75] text-[#59677c]"
        >
          {paragraph}
        </p>
      ))}

      {section.bullets && <BulletList items={section.bullets} />}

      {section.afterBullets && (
        <p className="mt-4 text-[16px] leading-[1.75] text-[#59677c]">
          {section.afterBullets}
        </p>
      )}

      {section.privacyLink && (
        <Link
          to="/privacy-policy"
          className="mt-3 inline-block text-[16px] font-semibold text-[#D32323] underline underline-offset-2 transition-colors hover:text-[#a91b1b]"
        >
          Read our Privacy Policy
        </Link>
      )}

      {section.email && (
        <a
          href={`mailto:${section.email}`}
          className="mt-3 inline-block text-[16px] font-semibold text-[#D32323] transition-colors hover:text-[#a91b1b]"
        >
          📧 {section.email}
        </a>
      )}
    </section>
  );
}

export default function TermsAndConditions() {
  return (
    <div className="min-h-screen bg-white">
      {/* EXISTING WEBSITE NAVBAR */}
      <Navbar />

      {/* TERMS & CONDITIONS CONTENT */}
      <main className="w-full bg-[#fafbff]">
        <div className="mx-auto w-full max-w-[1100px] px-5 py-12 sm:px-6 sm:py-14 lg:px-8 lg:py-16">

          {/* PAGE HEADER */}
          <div className="mb-10 border-b border-[#e3e9f0] pb-8">
            <h1 className="text-[36px] font-extrabold leading-[1.15] tracking-[-1px] text-[#10203b] sm:text-[40px]">
              Terms & Conditions
            </h1>

            <p className="mt-4 max-w-[750px] text-[16px] leading-[1.7] text-[#64748b]">
              Please read these Terms and Conditions carefully before
              using Biznep.
            </p>
          </div>

          {/* INTRODUCTION */}
          <div className="mb-2">
            <h2 className="text-[24px] font-bold leading-[1.3] tracking-[-0.3px] text-[#17233b]">
              Welcome to Biznep
            </h2>

            <p className="mt-4 text-[16px] leading-[1.75] text-[#59677c]">
              Welcome to Biznep ("we", "our", or "us"). These Terms and
              Conditions ("Terms") govern your access to and use of the
              Biznep mobile application, website, and related services
              (collectively, the "Service"). By accessing or using Biznep,
              you agree to be bound by these Terms. If you do not agree,
              please do not use our Service.
            </p>
          </div>

          {/* TERMS SECTIONS */}
          <div className="mt-4">
            {sections.map((section) => (
              <TermsSection
                key={section.number}
                section={section}
              />
            ))}
          </div>
        </div>
      </main>

      {/* EXISTING WEBSITE FOOTER */}
      <Footer />
    </div>
  );
}